import { useState } from 'react';
import { GradientDropdownStrength } from '@/components/generic/dropdown/GradientDropdown';
import {
  IroLikelihoodPickerFragment,
  LikelihoodValue,
  useUpdateIroLikelihoodMutation,
} from '@/graphql/generated';
import { useToast } from '@/components/layout/Toast';
import { useTranslation } from '@/hooks/useTranslation';
import i18n from '@/i18n';
import { useRefetchMaterialities } from './useRefetchMaterialities';
import { IroPickerDropdown } from '@/components/project/iro/evaluation/picker/IroPickerDropdown';

export type IroLikelihoodItem = {
  id: LikelihoodValue | null;
  name: string;
  strength: GradientDropdownStrength;
};

const likelihoodCriteriaChoicesMap = (): Map<
  LikelihoodValue | null,
  IroLikelihoodItem
> =>
  new Map([
    [
      LikelihoodValue.Real,
      {
        id: LikelihoodValue.Real,
        name: i18n.t(`enum:iroCriteria.likelihood.${LikelihoodValue.Real}`),
        strength: GradientDropdownStrength.VeryStrong,
      },
    ],
    [
      LikelihoodValue.Certain,
      {
        id: LikelihoodValue.Certain,
        name: i18n.t(`enum:iroCriteria.likelihood.${LikelihoodValue.Certain}`),
        strength: GradientDropdownStrength.VeryStrong,
      },
    ],
    [
      LikelihoodValue.VeryProbable,
      {
        id: LikelihoodValue.VeryProbable,
        name: i18n.t(
          `enum:iroCriteria.likelihood.${LikelihoodValue.VeryProbable}`,
        ),
        strength: GradientDropdownStrength.Strong,
      },
    ],
    [
      LikelihoodValue.Unlikely,
      {
        id: LikelihoodValue.Unlikely,
        name: i18n.t(`enum:iroCriteria.likelihood.${LikelihoodValue.Unlikely}`),
        strength: GradientDropdownStrength.Medium,
      },
    ],
    [
      LikelihoodValue.Impossible,
      {
        id: LikelihoodValue.Impossible,
        name: i18n.t(
          `enum:iroCriteria.likelihood.${LikelihoodValue.Impossible}`,
          { ns: 'enum' },
        ),
        strength: GradientDropdownStrength.Light,
      },
    ],
    [
      null,
      {
        id: null,
        name: '-',
        strength: GradientDropdownStrength.VeryLight,
      },
    ],
  ]);

export const likelihoodCriteriaChoices = () =>
  Array.from(likelihoodCriteriaChoicesMap().values());

export default function IroLikelihoodPicker({
  iro,
  readOnly,
}: {
  iro: IroLikelihoodPickerFragment;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const items = likelihoodCriteriaChoicesMap();
  const initialLikelihoodItem = items.get(
    iro.likelihoodCriteria?.value || null,
  );
  const [selected, setSelected] = useState(initialLikelihoodItem || null);
  const [update] = useUpdateIroLikelihoodMutation();
  const toast = useToast();
  const refetchQueries = useRefetchMaterialities();

  const chooseItemId = (itemId: string | null) => {
    const item = items.get(itemId as LikelihoodValue | null) || null;
    setSelected(item);

    update({
      variables: {
        input: {
          id: iro.id,
          likelihood: item?.id ?? null,
        },
      },
      refetchQueries,
    })
      .then(() => {
        toast.openToastWithMessage(
          t('toast:iro.criteria.update.success', {
            criteriaName: t('iro.criteria.likelihood.title'),
          }),
        );
      })
      .catch(() => {
        toast.openToastWithError(
          t('toast:iro.criteria.likelihood.update.error'),
        );
      });
  };

  return (
    <IroPickerDropdown<IroLikelihoodItem>
      items={items}
      selected={selected}
      chooseItemId={chooseItemId}
      assessmentStats={iro.assessmentStats?.likelihood ?? null}
      readOnly={readOnly}
    />
  );
}
